import { useState, useEffect, useCallback, useMemo } from "react";
import {
  getRewardPointsCount,
  getRewardLogs,
  getRewardActions,
  getRedeemHistory,
} from "../../../api/customer/customerFeatureApis";

// "REFER_FRIEND" -> "Refer Friend" (same formatting the mobile app uses)
export const formatRewardTitle = (value) =>
  String(value || "")
    .replaceAll("_", " ")
    .toLowerCase()
    .replace(/\b\w/g, (l) => l.toUpperCase());

/**
 * Loads the same four endpoints as the mobile Rewards screen:
 * /customer/rewards/count, /log, /customer/rewards (earn rules) and /redeem/history.
 * The backend answers SUCCESS (with an empty list) when there is nothing yet, so any other
 * status or a failed request is reported as an error instead of an empty page.
 */
const useCustomerRewards = (token) => {
  const [points, setPoints] = useState(0);
  const [logs, setLogs] = useState([]);
  const [actions, setActions] = useState([]);
  const [redemptions, setRedemptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const refetch = useCallback(async () => {
    if (!token) {
      setLoading(false);
      setError("You are not signed in. Please log in again.");
      return;
    }
    setLoading(true);
    setError(null);

    const results = await Promise.allSettled([
      getRewardPointsCount(token),
      getRewardLogs(token),
      getRewardActions(token),
      getRedeemHistory(token),
    ]);

    const failed = results.filter(
      (r) => r.status === "rejected" || r.value?.statusType !== "SUCCESS"
    );
    const [countRes, logRes, actionRes, redeemRes] = results.map((r) =>
      r.status === "fulfilled" && r.value?.statusType === "SUCCESS" ? r.value : null
    );

    if (countRes) setPoints(countRes.rewardPoints || 0);
    if (logRes) setLogs(logRes.userPointLogs || []);
    if (actionRes) setActions(actionRes.rewardActions || []);
    if (redeemRes) setRedemptions(redeemRes.redeemRequests || []);

    if (failed.length > 0) {
      const first = failed[0];
      const reason =
        first.status === "rejected"
          ? first.reason?.response?.data?.text || first.reason?.message
          : first.value?.text;
      setError(reason ? `Unable to load rewards: ${reason}` : "Unable to load rewards. Please try again.");
    }
    setLoading(false);
  }, [token]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  // Same totals as mobile: earned = sum of positive log points, redeemed = sum of negative ones
  const totals = useMemo(
    () => ({
      earned: logs.filter((l) => l.points > 0).reduce((sum, l) => sum + l.points, 0),
      redeemed: logs.filter((l) => l.points < 0).reduce((sum, l) => sum + Math.abs(l.points), 0),
    }),
    [logs]
  );

  return { points, logs, actions, redemptions, totals, loading, error, refetch };
};

export default useCustomerRewards;
